import { useEffect, useState } from "react";
import { UserPlus, Users, CheckCircle, Search, Loader2, ChevronRight, Phone, Fingerprint, Settings } from "lucide-react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import type { Client } from "../../types";
import { navigate } from "../../lib/router";
import { UpcomingBirthdays } from "./components/UpcomingBirthdays";
import { NewClientModal } from "./components/NewClientModal";
import { parseBrazilDate } from "../utils/date";
import { useLanguage } from "../../contexts/LanguageContext";
import { clientsLabels } from "./labels";

export function ClientsPage() {
  const [items, setItems] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [nextRenewal, setNextRenewal] = useState<string | null>(null);
  const [activeClients, setActiveClients] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { lang } = useLanguage();
  const lbl = clientsLabels[lang];

  const load = async () => {
    const [clientsRes, companyRes] = await Promise.all([
      api.listClients(),
      api.getCompany(),
    ]);
    if (clientsRes.ok) {
      setItems(clientsRes.data);
      const activeResponses = await Promise.all(
        clientsRes.data.map(c => api.listClientRecurringPlans(c.uid))
      );
      const active = activeResponses.reduce(
        (acc, res) => (res.ok && res.data.some(p => p.active) ? acc + 1 : acc),
        0,
      );
      setActiveClients(active);
    }
    if (companyRes.ok) setNextRenewal(companyRes.data.next_renewal ?? null);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleNewClient = () => {
    if (loading) return;
    const hasActivePlan = nextRenewal ? parseBrazilDate(nextRenewal) > new Date() : false;
    if (items.length >= 10 && !hasActivePlan) {
      navigate("/app/paywall");
      return;
    }
    setIsModalOpen(true);
  };

  const normalizedQuery = query.toLowerCase();
  const numericQuery = query.replace(/\D/g, "");
  const filtered = items
    .filter(c => {
      const nameMatch = c.name.toLowerCase().includes(normalizedQuery);
      const cpfMatch = numericQuery ? (c.cpf ?? "").replace(/\D/g, "").includes(numericQuery) : false;
      const phoneMatch = numericQuery ? (c.phone ?? "").replace(/\D/g, "").includes(numericQuery) : false;
      return nameMatch || cpfMatch || phoneMatch;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 p-4 md:p-6 animate-in fade-in duration-500">
      <UpcomingBirthdays clients={items} loading={loading} />

      <section className="space-y-6">
        {/* Header Area */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-800 tracking-tight">{lbl.title}</h1>
            <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest leading-none">
              {items.length} {lbl.title.toLowerCase()}
            </p>
          </div>
          <div className="flex items-center gap-3">
             <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="search"
                  placeholder={lbl.search.placeholder}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  className="w-full pl-11 h-11 rounded-2xl border-neutral-100 bg-white focus-visible:ring-emerald-500/20 font-medium"
                />
             </div>
              <Button 
                onClick={handleNewClient} 
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 px-6 rounded-2xl transition-all active:scale-95 flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">{lbl.actions.new}</span>
              </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-[2.5rem] border border-neutral-100 p-8 flex items-center gap-6 shadow-sm transition-all hover:border-emerald-100 group">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform duration-500">
              <Users className="w-7 h-7" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">{lbl.stats.registered}</p>
              <p className="text-3xl font-bold text-neutral-800 tracking-tight">{items.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-[2.5rem] border border-neutral-100 p-8 flex items-center gap-6 shadow-sm transition-all hover:border-emerald-100 group">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform duration-500">
              <CheckCircle className="w-7 h-7" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">{lbl.stats.active}</p>
              <p className="text-3xl font-bold text-neutral-800 tracking-tight">{activeClients}</p>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-[2.5rem] border border-neutral-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-24 flex flex-col items-center gap-4">
              <div className="h-10 w-10 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin" />
              <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest">{lbl.loading}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-24 flex flex-col items-center justify-center gap-4 text-center">
               <div className="w-20 h-20 rounded-[2.5rem] bg-neutral-50 flex items-center justify-center mb-2">
                  <Users className="h-10 w-10 text-neutral-300" />
               </div>
               <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest leading-relaxed">
                  {items.length === 0 ? lbl.none : lbl.noneFound}
               </p>
               {items.length === 0 && (
                  <Button 
                    variant="outline" 
                    onClick={handleNewClient}
                    className="h-12 px-8 rounded-2xl border-neutral-100 font-bold uppercase text-[10px] tracking-widest hover:bg-neutral-50 transition-all"
                  >
                    {lbl.actions.new}
                  </Button>
               )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b border-neutral-50">
                    <TableHead className="h-16 px-8 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                       <div className="flex items-center gap-2">
                          <Settings className="w-3 h-3" />
                          {lang === 'pt' ? 'Nome do Paciente' : 'Patient Name'}
                       </div>
                    </TableHead>
                    <TableHead className="h-16 px-8 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                       <div className="flex items-center gap-2">
                          <Fingerprint className="w-3 h-3" />
                          CPF
                       </div>
                    </TableHead>
                    <TableHead className="h-16 px-8 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                       <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          {lang === 'pt' ? 'Telefone' : 'Phone'}
                       </div>
                    </TableHead>
                    <TableHead className="h-16 px-8 text-right text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                      {lang === 'pt' ? 'Ações' : 'Actions'}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => (
                    <TableRow 
                      key={c.id} 
                      className="group hover:bg-neutral-50/50 transition-colors border-b border-neutral-50 last:border-0 cursor-pointer"
                      onClick={() => navigate(`/app/clients/${c.uid}`)}
                    >
                      <TableCell className="px-8 py-5">
                         <span className="font-bold text-neutral-800 text-sm group-hover:text-emerald-700 transition-colors">
                            {c.name}
                         </span>
                      </TableCell>
                      <TableCell className="px-8 py-5 text-neutral-500 text-xs font-medium">
                        {c.cpf || "—"}
                      </TableCell>
                      <TableCell className="px-8 py-5 text-neutral-500 text-xs font-medium">
                        {c.phone || "—"}
                      </TableCell>
                      <TableCell className="px-8 py-5 text-right">
                        <Button
                          variant="ghost"
                          className="h-9 px-4 font-bold text-[10px] uppercase tracking-widest text-emerald-600 hover:text-white hover:bg-emerald-600 rounded-xl transition-all"
                        >
                          {lbl.actions.open}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </section>

      <NewClientModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={load}
      />
    </div>
  );
}
